/**
 * Tutoring tools for the AI: it can look up students, tutors and the schedule, and propose
 * logging a session, a payment, or calendar times. Proposals are shown as a card with the
 * numbers editable, and nothing is saved until you confirm.
 */
import type { EditableField } from "./editable.ts";
import {
  hoursText,
  sessionCharge,
  studentSummary,
  tutorSummary,
  type Tutor,
  type TutoringEntry,
  type TutoringPackage,
  type TutoringSlot,
  type TutoringStudent,
} from "../tutoring.ts";

export interface TutoringContext {
  students: TutoringStudent[];
  tutors: Tutor[];
  packages: TutoringPackage[];
  entries: TutoringEntry[];
  slots: TutoringSlot[];
  today: string;
}

export interface ApiCall {
  table: "entries" | "slots" | "money";
  /** Where it goes when it isn't a tutoring table (Money changes). */
  path?: string;
  body: Record<string, unknown>;
}

export type TutoringOutcome =
  | { kind: "result"; data: unknown; label: string }
  | {
      kind: "api-proposal";
      summary: string;
      calls: ApiCall[];
      fields: EditableField[];
      label: string;
    }
  | { kind: "error"; message: string; label: string };

const fn = (
  name: string,
  description: string,
  properties: Record<string, unknown>,
  required: string[] = [],
) => ({
  type: "function" as const,
  function: { name, description, parameters: { type: "object", properties, required } },
});

export const TUTORING_TOOL_DEFS = [
  fn(
    "tutoring_overview",
    "Tutoring: every student (rate, hours left, what they owe), tutors (what you owe them) and the next two weeks of the calendar.",
    {},
  ),
  fn(
    "tutoring_sessions",
    'Tutoring: every lesson between two dates, taught or on the calendar, filtered by tutor and/or student, with the exact date, weekday, times, hours and whether it was done, is booked or didn\'t happen. Use it for any question about when lessons were or will be ("when did Jianing teach this month"). Dates and weekdays come from here; never work them out yourself.',
    {
      tutor: { type: "string", description: 'Tutor\'s name; "Miles" for lessons you taught' },
      student: { type: "string" },
      from: { type: "string", description: "YYYY-MM-DD; defaults to the 1st of this month" },
      to: { type: "string", description: "YYYY-MM-DD; defaults to today" },
    },
  ),
  fn(
    "propose_log_session",
    "Tutoring: propose logging a session that happened. The user must confirm.",
    {
      student: { type: "string" },
      minutes: { type: "integer", description: "Length in minutes, e.g. 60 or 90" },
      date: { type: "string", description: "YYYY-MM-DD, defaults to today" },
      tutor: { type: "string", description: "Tutor's name; leave out if Miles taught it" },
    },
    ["student", "minutes"],
  ),
  fn(
    "propose_tutoring_payment",
    "Tutoring: propose recording a payment from a family. The user must confirm.",
    {
      student: { type: "string" },
      amount: { type: "number", description: "Dollars" },
      method: { type: "string", enum: ["zelle", "cash", "card", "other"] },
      date: { type: "string", description: "YYYY-MM-DD, defaults to today" },
    },
    ["student", "amount"],
  ),
  fn(
    "propose_add_time",
    "Tutoring: propose lessons for a student on the calendar (a past one is logged as done), optionally repeating weekly. The tutor is a tutor's name, left out when Miles teaches it, or needs_tutor for a lesson any tutor can take. The user must confirm.",
    {
      date: { type: "string", description: "YYYY-MM-DD of the first one" },
      start: { type: "string", description: "HH:MM, 24-hour" },
      end: { type: "string", description: "HH:MM, 24-hour" },
      student: { type: "string" },
      tutor: { type: "string", description: "Leave out when Miles teaches it" },
      needs_tutor: { type: "boolean", description: "No tutor yet: any tutor can take it" },
      repeat_weeks: { type: "integer", description: "How many weeks in a row, default 1" },
    },
    ["date", "start", "end", "student"],
  ),
] as const;

export const TUTORING_TOOL_NAMES = new Set<string>(TUTORING_TOOL_DEFS.map((t) => t.function.name));

function find<T extends { name: string; archived?: boolean }>(
  list: T[],
  name: unknown,
): T | undefined {
  if (typeof name !== "string" || !name.trim()) return undefined;
  const q = name.trim().toLowerCase();
  const live = list.filter((x) => !x.archived);
  return (
    live.find((x) => x.name.toLowerCase() === q) ??
    live.find((x) => x.name.toLowerCase().startsWith(q))
  );
}

const WEEKDAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
/** The weekday of a date, worked out here so the model never has to ("2026-10-22" -> Thursday). */
export const weekdayOf = (date: string) => WEEKDAYS[new Date(`${date}T12:00:00Z`).getUTCDay()]!;

interface Lesson {
  date: string;
  weekday: string;
  time: string | null;
  hours: string;
  minutes: number;
  tutor: string;
  student: string | null;
  status: "done" | "booked" | "didn't happen";
}

/** Every lesson in a date range: logged sessions (with their calendar times when they have one) and booked or missed calendar lessons. */
export function lessonsBetween(
  ctx: TutoringContext,
  from: string,
  to: string,
  tutorId: string | null | undefined,
  studentId: string | undefined,
): Lesson[] {
  const tutorName = (id: string | null) =>
    id ? (ctx.tutors.find((t) => t.id === id)?.name ?? "a tutor") : "Miles";
  const studentName = (id: string | null) =>
    id ? (ctx.students.find((s) => s.id === id)?.name ?? null) : null;
  const keep = (date: string, tid: string | null, sid: string | null) =>
    date >= from &&
    date <= to &&
    (tutorId === undefined || (tid ?? null) === tutorId) &&
    (studentId === undefined || sid === studentId);
  const out: Lesson[] = [];
  const slotById = new Map(ctx.slots.map((s) => [s.id, s]));
  const logged = new Set<string>();
  for (const e of ctx.entries) {
    if (e.kind !== "session" || !keep(e.on, e.tutorId, e.studentId)) continue;
    const slot = e.slotId ? slotById.get(e.slotId) : undefined;
    if (slot) logged.add(slot.id);
    out.push({
      date: e.on,
      weekday: weekdayOf(e.on),
      time: slot ? `${slot.startsAt.slice(11, 16)}–${slot.endsAt.slice(11, 16)}` : null,
      hours: hoursText(e.minutes),
      minutes: e.minutes,
      tutor: tutorName(e.tutorId),
      student: studentName(e.studentId),
      status: "done",
    });
  }
  for (const s of ctx.slots) {
    const date = s.startsAt.slice(0, 10);
    if (logged.has(s.id) || !s.studentId || !keep(date, s.tutorId, s.studentId)) continue;
    const missed = s.status === "cancelled" && s.outcome === "missed";
    // A done lesson with no session left was taken back off; open and cancelled ones aren't lessons.
    if (s.status !== "booked" && !missed) continue;
    const minutes = Math.round(
      (Date.parse(`${s.endsAt}:00Z`) - Date.parse(`${s.startsAt}:00Z`)) / 60_000,
    );
    out.push({
      date,
      weekday: weekdayOf(date),
      time: `${s.startsAt.slice(11, 16)}–${s.endsAt.slice(11, 16)}`,
      hours: hoursText(minutes),
      minutes,
      tutor: tutorName(s.tutorId),
      student: studentName(s.studentId),
      status: missed ? "didn't happen" : "booked",
    });
  }
  return out.sort((a, b) => (a.date + (a.time ?? "") < b.date + (b.time ?? "") ? -1 : 1));
}

const money = (c: number) => `$${(c / 100).toFixed(2)}`;
const isDate = (v: unknown): v is string => typeof v === "string" && /^\d{4}-\d{2}-\d{2}$/.test(v);
const isTime = (v: unknown): v is string => typeof v === "string" && /^\d{2}:\d{2}$/.test(v);

export function runTutoringTool(name: string, raw: unknown, ctx: TutoringContext): TutoringOutcome {
  const a = (raw && typeof raw === "object" ? raw : {}) as Record<string, unknown>;
  switch (name) {
    case "tutoring_overview": {
      const soon = new Date(Date.parse(`${ctx.today}T12:00:00Z`) + 14 * 86_400_000)
        .toISOString()
        .slice(0, 10);
      return {
        kind: "result",
        label: "Looked at tutoring",
        data: {
          today: ctx.today,
          todayWeekday: weekdayOf(ctx.today),
          students: ctx.students
            .filter((s) => !s.archived)
            .map((s) => {
              const sum = studentSummary(s.id, ctx.entries);
              return {
                name: s.name,
                family: s.family,
                rate: `${money(s.rateCashCents)}/hr cash${s.rateCardCents ? `, ${money(s.rateCardCents)}/hr card` : ""}`,
                grandfathered: s.grandfathered,
                billing: s.billing,
                hoursLeft: sum.boughtMinutes ? hoursText(sum.leftMinutes) : null,
                owes: money(sum.balanceCents),
                lastSession: sum.lastSession,
              };
            }),
          tutors: ctx.tutors
            .filter((t) => !t.archived)
            .map((t) => ({
              name: t.name,
              owedByYou: money(tutorSummary(t, ctx.entries).owedCents),
            })),
          upcoming: ctx.slots
            .filter(
              (s) =>
                s.status !== "cancelled" &&
                s.startsAt.slice(0, 10) >= ctx.today &&
                s.startsAt.slice(0, 10) <= soon,
            )
            .map((s) => ({
              when: s.startsAt.replace("T", " "),
              weekday: weekdayOf(s.startsAt.slice(0, 10)),
              until: s.endsAt.slice(11),
              student: ctx.students.find((x) => x.id === s.studentId)?.name ?? null,
              tutor: ctx.tutors.find((x) => x.id === s.tutorId)?.name ?? "Miles",
              status: s.status,
            })),
        },
      };
    }
    case "tutoring_sessions": {
      const isMiles = typeof a.tutor === "string" && /^(miles|me|you)\b/i.test(a.tutor.trim());
      const tutor = a.tutor && !isMiles ? find(ctx.tutors, a.tutor) : undefined;
      if (a.tutor && !isMiles && !tutor)
        return {
          kind: "error",
          label: "Couldn't find that tutor",
          message: `No tutor called ${String(a.tutor)}. Tutors: ${
            ctx.tutors
              .filter((t) => !t.archived)
              .map((t) => t.name)
              .join(", ") || "none"
          }.`,
        };
      const student = a.student ? find(ctx.students, a.student) : undefined;
      if (a.student && !student)
        return {
          kind: "error",
          label: "Couldn't find that student",
          message: `No student called ${String(a.student)}.`,
        };
      const from = isDate(a.from) ? a.from : `${ctx.today.slice(0, 7)}-01`;
      const to = isDate(a.to) ? a.to : ctx.today;
      const lessons = lessonsBetween(ctx, from, to, isMiles ? null : tutor?.id, student?.id);
      const done = lessons.filter((l) => l.status === "done");
      return {
        kind: "result",
        label: `Looked up ${tutor?.name ?? (isMiles ? "your" : "all")} lessons, ${from} to ${to}`,
        data: {
          from,
          fromWeekday: weekdayOf(from),
          to,
          toWeekday: weekdayOf(to),
          today: ctx.today,
          todayWeekday: weekdayOf(ctx.today),
          tutor: tutor?.name ?? (isMiles ? "Miles" : "everyone"),
          student: student?.name ?? "everyone",
          doneCount: done.length,
          doneHours: hoursText(done.reduce((t, l) => t + l.minutes, 0)),
          lessons,
          note: lessons.length
            ? "Use these dates and weekdays exactly as given."
            : "No lessons in this range. Say so; don't guess.",
        },
      };
    }
    case "propose_log_session": {
      const s = find(ctx.students, a.student);
      if (!s)
        return {
          kind: "error",
          label: "Couldn't find that student",
          message: `No student called ${String(a.student)}.`,
        };
      const minutes = Math.round(Number(a.minutes));
      if (!(minutes > 0 && minutes <= 600))
        return {
          kind: "error",
          label: "Odd session length",
          message: "Minutes should be between 1 and 600.",
        };
      const tutor = a.tutor ? find(ctx.tutors, a.tutor) : undefined;
      if (a.tutor && !tutor)
        return {
          kind: "error",
          label: "Couldn't find that tutor",
          message: `No tutor called ${String(a.tutor)}.`,
        };
      const on = isDate(a.date) ? a.date : ctx.today;
      const cents = s.billing === "hourly" ? sessionCharge(s, minutes) : 0;
      return {
        kind: "api-proposal",
        label: `Proposed a session for ${s.name}`,
        summary: `Log ${hoursText(minutes)} with ${s.name} on ${on}${tutor ? ` (taught by ${tutor.name})` : ""}${cents ? `, charging ${money(cents)}` : ""}.`,
        calls: [
          {
            table: "entries",
            body: {
              kind: "session",
              studentId: s.id,
              tutorId: tutor?.id ?? null,
              on,
              minutes,
              cents,
              note: "",
            },
          },
        ],
        fields: [
          { key: "0.minutes", label: "Minutes", kind: "text", value: String(minutes) },
          { key: "0.on", label: "Date", kind: "date", value: on },
          ...(s.billing === "hourly"
            ? [
                {
                  key: "0.cents",
                  label: "Charge",
                  kind: "money" as const,
                  value: (cents / 100).toFixed(2),
                },
              ]
            : []),
        ],
      };
    }
    case "propose_tutoring_payment": {
      const s = find(ctx.students, a.student);
      if (!s)
        return {
          kind: "error",
          label: "Couldn't find that student",
          message: `No student called ${String(a.student)}.`,
        };
      const cents = Math.round(Number(a.amount) * 100);
      if (!(cents > 0))
        return {
          kind: "error",
          label: "Odd amount",
          message: "The amount should be more than $0.",
        };
      const method = ["zelle", "cash", "card", "other"].includes(String(a.method))
        ? String(a.method)
        : "zelle";
      const on = isDate(a.date) ? a.date : ctx.today;
      return {
        kind: "api-proposal",
        label: `Proposed a payment from ${s.name}'s family`,
        summary: `Record ${money(cents)} from ${s.name}'s family by ${method} on ${on}.`,
        calls: [
          {
            table: "entries",
            body: { kind: "payment", studentId: s.id, on, cents, method, note: "" },
          },
        ],
        fields: [
          { key: "0.cents", label: "Amount", kind: "money", value: (cents / 100).toFixed(2) },
          { key: "0.on", label: "Date", kind: "date", value: on },
        ],
      };
    }
    case "propose_add_time": {
      if (!isDate(a.date) || !isTime(a.start) || !isTime(a.end) || a.end <= a.start)
        return {
          kind: "error",
          label: "Couldn't read the time",
          message: "Give date YYYY-MM-DD and start/end HH:MM (end after start).",
        };
      // Lessons are always for a student (2026-11-02): tutors take students, families don't book.
      const student = find(ctx.students, a.student);
      if (!student)
        return {
          kind: "error",
          label: "Couldn't find that student",
          message: a.student ? `No student called ${String(a.student)}.` : "Say which student.",
        };
      const tutor = a.tutor ? find(ctx.tutors, a.tutor) : undefined;
      if (a.tutor && !tutor)
        return {
          kind: "error",
          label: "Couldn't find that tutor",
          message: `No tutor called ${String(a.tutor)}.`,
        };
      const needs = !tutor && a.needs_tutor === true;
      const weeks = Math.max(1, Math.min(26, Math.round(Number(a.repeat_weeks) || 1)));
      const calls: ApiCall[] = [
        {
          table: "slots",
          path: "/api/tutoring/lessons",
          body: {
            studentId: student.id,
            tutorId: tutor?.id ?? null,
            unclaimed: needs,
            date: a.date,
            start: a.start,
            end: a.end,
            weeks,
            note: "",
          },
        },
      ];
      return {
        kind: "api-proposal",
        label: "Proposed lessons",
        summary: `Book ${student.name} ${a.date} ${a.start}–${a.end}${tutor ? ` with ${tutor.name}` : needs ? ", needs a tutor" : ", with you"}${weeks > 1 ? `, every week for ${weeks} weeks` : ""}.`,
        calls,
        fields: [],
      };
    }
  }
  return { kind: "error", label: `Unknown tool ${name}`, message: "That tool doesn't exist." };
}

/** The calls with your edits applied (minutes, date, amount); an error when one can't be read. */
export function applyApiEdits(
  calls: readonly ApiCall[],
  edits: Record<string, string>,
): { ok: true; calls: ApiCall[] } | { ok: false; message: string } {
  const out = structuredClone(calls) as ApiCall[];
  for (const [key, raw] of Object.entries(edits)) {
    const [i, field] = key.split(".");
    const call = out[Number(i)];
    if (!call || !field) continue;
    const v = raw.trim();
    if (field === "minutes") {
      const n = Math.round(Number(v));
      if (!(n > 0)) return { ok: false, message: "Minutes should be a number above 0" };
      call.body.minutes = n;
    } else if (field === "cents") {
      const t = v.replace(/[$,\s]/g, "");
      if (!/^\d+(\.\d{1,2})?$/.test(t))
        return { ok: false, message: "Enter the amount like 40.00" };
      call.body.cents = Math.round(Number(t) * 100);
    } else if (field === "on") {
      if (!isDate(v)) return { ok: false, message: "Dates look like 2026-10-12" };
      call.body.on = v;
    }
  }
  return { ok: true, calls: out };
}
